/**
 * Dynamic QR Code Handler
 * Manages the complete flow of dynamic QR code generation and validation
 */

import indexedDBManager from './indexedDBManager.js';
import advancedFingerprint from './advancedFingerprint.js';

class DynamicQRHandler {
    constructor() {
        this.isInitialized = false;
        this.currentQRHash = null;
        this.validationInProgress = false;
    }

    /**
     * Initialize the handler
     */
    async init() {
        if (this.isInitialized) return;

        try {
            await indexedDBManager.init();
            this.isInitialized = true;
            console.log('Dynamic QR Handler initialized');

            // Clean up old QR codes on init
            await indexedDBManager.cleanupOldQRCodes(7);
        } catch (error) {
            console.error('Failed to initialize Dynamic QR Handler:', error);
            throw error;
        }
    }

    /**
     * Handle initial QR code scan
     */
    async handleInitialScan(originalHash) {
        try {
            await this.init();

            // Check if we already have this QR code in IndexedDB
            const existingData = await indexedDBManager.getQRData(originalHash);
            if (existingData) {
                console.log('QR code already exists in IndexedDB');
                return await this.validateExistingQR(originalHash);
            }

            // Call server to generate dynamic hash
            // Collect advanced fingerprint signals
            console.log('🔍 Collecting advanced fingerprint signals...');
            const signals = await advancedFingerprint.collect();
            const fingerprint = await advancedFingerprint.getSimpleHash(); // Backward compatibility
            console.log('✅ Advanced fingerprint collected:', {
                canvas: !!signals.canvas,
                webgl: !!signals.webgl,
                audio: !!signals.audio,
                fonts: signals.fonts?.length || 0,
                isIncognito: signals.isIncognito
            });

            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            console.log('CSRF token found:', !!csrfToken);

            const response = await fetch(`/api/qr/initial-scan/${originalHash}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    'X-Browser-Fingerprint': fingerprint,
                    'X-Browser-Fingerprint-Data': JSON.stringify(signals)
                },
                body: JSON.stringify({
                    browser_fingerprint: fingerprint,
                    timestamp: Date.now()
                })
            });

            console.log('Response status:', response.status);
            const result = await response.json();
            console.log('Server response:', result);

            if (!result.success) {
                const error = new Error(result.message || 'Failed to process QR code');
                error.code = result.message; // Preserve the error code for campaign_ended
                throw error;
            }

            // Store in IndexedDB
            await indexedDBManager.storeQRData(
                result.dynamic_hash,
                result.browser_storage_data.fingerprint,
                {
                    original_hash: originalHash,
                    server_timestamp: result.browser_storage_data.timestamp,
                    redirect_url: result.redirect_url,
                    is_rescan: result.rescan || false
                }
            );

            this.currentQRHash = result.dynamic_hash;

            // Add a small delay for rescan to ensure DB transaction is committed
            if (result.rescan) {
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            // Always store a flag in sessionStorage to skip validation for newly created/retrieved QRs
            // This handles both new QRs and existing QRs that were just retrieved
            sessionStorage.setItem('skipQRValidation', result.dynamic_hash);
            console.log('Set skipQRValidation flag for:', result.dynamic_hash);

            return {
                success: true,
                dynamicHash: result.dynamic_hash,
                redirectUrl: result.redirect_url,
                isNew: !result.existing,
                isRescan: result.rescan || false
            };

        } catch (error) {
            console.error('Initial scan failed:', error);
            console.log('Error details - message:', error.message, 'code:', error.code);

            const errorResponse = {
                success: false,
                error: error.message,
                message: error.code || error.message // Include the error code for campaign_ended
            };

            console.log('Returning error response:', errorResponse);
            return errorResponse;
        }
    }

    /**
     * Validate existing QR code
     */
    async validateExistingQR(qrHash) {
        try {
            // First check local storage
            const localData = await indexedDBManager.getQRData(qrHash);
            if (!localData) {
                return {
                    success: false,
                    error: 'QR code not found in local storage'
                };
            }

            // Check if already marked as invalid, scanned, or completed
            if (localData.status === 'invalid') {
                return {
                    success: false,
                    error: 'QR code is no longer valid'
                };
            }

            if (localData.status === 'scanned') {
                return {
                    success: false,
                    error: 'QR code has already been used',
                    code: 'SCANNED'
                };
            }

            if (localData.status === 'completed') {
                return {
                    success: false,
                    error: 'Participation already completed',
                    code: 'COMPLETED'
                };
            }

            // Validate with server
            const serverValidation = await indexedDBManager.validateWithServer(qrHash);
            
            if (!serverValidation.valid) {
                return {
                    success: false,
                    error: serverValidation.reason
                };
            }

            this.currentQRHash = qrHash;

            return {
                success: true,
                dynamicHash: qrHash,
                redirectUrl: localData.redirect_url || `/qr/${qrHash}`,
                isNew: false
            };

        } catch (error) {
            console.error('QR validation failed:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Validate access to dynamic QR code
     */
    async validateAccess(dynamicHash) {
        if (this.validationInProgress) {
            return { success: false, error: 'Validation already in progress' };
        }

        this.validationInProgress = true;

        try {
            await this.init();

            // Check IndexedDB first
            const localValidation = await this.validateExistingQR(dynamicHash);
            if (!localValidation.success) {
                return localValidation;
            }

            // Call server validation with advanced fingerprinting
            const signals = await advancedFingerprint.collect();
            const fingerprint = await advancedFingerprint.getSimpleHash();

            const response = await fetch(`/api/qr/validate/${dynamicHash}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
                    'X-Browser-Fingerprint': fingerprint,
                    'X-Browser-Fingerprint-Data': JSON.stringify(signals)
                },
                body: JSON.stringify({
                    browser_fingerprint: fingerprint
                })
            });

            const result = await response.json();

            if (!result.success) {
                // Update local status
                await indexedDBManager.updateQRStatus(dynamicHash, 'invalid', {
                    server_error: result.message,
                    error_code: result.code
                });

                return {
                    success: false,
                    error: result.message,
                    code: result.code
                };
            }

            // Update access count in local storage
            await indexedDBManager.updateQRStatus(dynamicHash, 'active', {
                last_validated: new Date().toISOString(),
                server_qr_id: result.qr_code_id
            });

            this.currentQRHash = dynamicHash;

            return {
                success: true,
                qrCodeId: result.qr_code_id,
                participationCompleted: result.participation_completed
            };

        } catch (error) {
            console.error('Access validation failed:', error);
            return {
                success: false,
                error: error.message
            };
        } finally {
            this.validationInProgress = false;
        }
    }

    /**
     * Mark participation as completed
     */
    async markParticipationCompleted(participantId) {
        try {
            // Get the current QR hash from the URL as a fallback
            const urlPath = window.location.pathname;
            const urlQRHash = urlPath.startsWith('/') ? urlPath.substring(1) : urlPath;

            console.log('Marking participation as completed - DEBUGGING', {
                currentQRHash: this.currentQRHash,
                urlQRHash: urlQRHash,
                participantId: participantId,
                currentURL: window.location.href
            });

            // Use URL QR hash if currentQRHash is not set or seems wrong
            let qrHashToUse = this.currentQRHash;
            if (!qrHashToUse || qrHashToUse.length < 5) {
                console.warn('Using QR hash from URL as fallback:', urlQRHash);
                qrHashToUse = urlQRHash;
            }

            if (!qrHashToUse) {
                console.error('No active QR code for completion');
                throw new Error('No active QR code');
            }

            // Call server to mark completion
            console.log('Calling /api/qr/mark-completed endpoint with QR hash:', qrHashToUse);
            const response = await fetch('/api/qr/mark-completed', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                },
                body: JSON.stringify({
                    qr_hash: qrHashToUse,
                    participant_id: participantId
                })
            });

            console.log('Mark completion response status:', response.status);
            const result = await response.json();
            console.log('Mark completion response:', result);

            if (!result.success) {
                console.error('Failed to mark completion:', result.message);
                throw new Error(result.message);
            }

            console.log('Participation marked as completed successfully');

            // Update local storage
            await indexedDBManager.updateQRStatus(qrHashToUse, 'completed', {
                participant_id: participantId,
                completed_at: new Date().toISOString()
            });

            // Update the current QR hash to the correct one
            this.currentQRHash = qrHashToUse;

            console.log('Participation marked as completed');
            return { success: true };

        } catch (error) {
            console.error('Failed to mark participation completed:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get current QR code status
     */
    async getCurrentQRStatus() {
        if (!this.currentQRHash) {
            return null;
        }

        try {
            const localData = await indexedDBManager.getQRData(this.currentQRHash);
            return localData;
        } catch (error) {
            console.error('Failed to get QR status:', error);
            return null;
        }
    }

    /**
     * Check if user has access to any QR code
     */
    async hasValidQRAccess() {
        try {
            await this.init();
            const allQRCodes = await indexedDBManager.getAllQRCodes();
            
            for (const qrData of allQRCodes) {
                if (qrData.status === 'active') {
                    const validation = await indexedDBManager.validateWithServer(qrData.qr_hash);
                    if (validation.valid) {
                        this.currentQRHash = qrData.qr_hash;
                        return {
                            hasAccess: true,
                            qrHash: qrData.qr_hash
                        };
                    }
                }
            }

            return { hasAccess: false };

        } catch (error) {
            console.error('Failed to check QR access:', error);
            return { hasAccess: false };
        }
    }

    /**
     * Handle URL hash change for dynamic QR codes
     */
    async handleURLHashChange() {
        const currentHash = window.location.hash.substring(1); // Remove #
        
        if (!currentHash) return;

        // Check if this looks like a QR hash (10 characters, alphanumeric)
        if (/^[A-Z0-9]{10}$/.test(currentHash)) {
            console.log('Detected dynamic QR hash in URL:', currentHash);
            
            const validation = await this.validateAccess(currentHash);
            
            if (!validation.success) {
                console.error('QR validation failed:', validation.error);

                // For browser mismatch, silently redirect to welcome page
                if (validation.code === 'BROWSER_MISMATCH') {
                    window.location.href = '/';
                    return false;
                }

                // For other errors, show access denied message
                this.showAccessDeniedMessage(validation.error);
                return false;
            }

            return true;
        }

        return false;
    }

    /**
     * Show access denied message
     */
    showAccessDeniedMessage(reason) {
        // Use iziToast for better UX (already imported in app.js)
        if (typeof iziToast !== 'undefined') {
            let message = 'Access not allowed';
            let title = 'Access Denied';

            // Map specific errors to user-friendly messages
            if (reason && reason.toLowerCase().includes('server')) {
                message = 'Server temporarily unavailable';
                title = 'Server Error';
            } else if (reason && reason.toLowerCase().includes('network')) {
                message = 'Network connection error. Please check your internet.';
                title = 'Connection Error';
            } else if (reason && reason.toLowerCase().includes('expired')) {
                message = 'This QR code has expired';
                title = 'QR Code Expired';
            } else if (reason && reason.toLowerCase().includes('invalid')) {
                message = 'Invalid QR code';
                title = 'Invalid Code';
            }

            iziToast.error({
                title: title,
                message: message,
                position: 'topCenter',
                timeout: 6000,
                titleColor: '#ff6b35',
                messageColor: '#ffffff',
                backgroundColor: 'rgba(239, 68, 68, 0.95)',
                progressBarColor: '#ff6b35',
                close: false,
                overlay: true
            });

            // Show helpful suggestion after 1 second
            setTimeout(() => {
                iziToast.info({
                    title: 'Help',
                    message: 'Please try again later',
                    position: 'topCenter',
                    timeout: 4000,
                    backgroundColor: 'rgba(59, 130, 246, 0.95)'
                });
            }, 1000);
        } else {
            // Fallback to console if iziToast is not available
            console.error('Access Denied:', reason);
        }
        
        // Redirect to home page after 4 seconds (longer for users to read)
        setTimeout(() => {
            window.location.href = '/';
        }, 4000);
    }

    /**
     * Initialize URL monitoring
     */
    initURLMonitoring() {
        // Monitor hash changes
        window.addEventListener('hashchange', () => {
            this.handleURLHashChange();
        });

        // Check initial URL
        this.handleURLHashChange();
    }
}

// Export singleton instance
export default new DynamicQRHandler();
